// The outside REST API, for the people who use it: their own bearer keys, the
// PWAs those keys provisioned (POST /api/v1/public/pwa), and the docs. Opened
// by the `public-api` section, which the `api` addon grants; a super admin
// sees everyone's keys and provisions with the owner named.
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/auth';
import { PublicApiKeys, DOCS_URL } from '@/components/PublicApiKeys';
import { listPwaProvisions, type PwaProvisionRow } from '@/api/public-api-keys';
import { formatDateTime } from '@/lib/datetime';

const PLATFORM_LABEL: Record<PwaProvisionRow['platform'], string> = { android: 'Android', ios: 'iOS' };

export function PublicApi() {
  const admin = useAuthStore((s) => s.admin);
  const showOwner = admin?.role === 'super_admin';
  const { data: rows = [], isLoading, isError, error } = useQuery({
    queryKey: ['pwa-provisions'],
    queryFn: () => listPwaProvisions(200),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-content sm:text-2xl">Public API</h1>
        <p className="mt-1 max-w-2xl text-sm text-content-subtle">
          The REST API for scripts and bots: the released-apps catalogue with texts, icon and screenshots,
          the weekly "advertised" stamp, and one-call PWA provisioning per project, GEO and platform.
          Contract and a try-it-out console:{' '}
          <a href={DOCS_URL} target="_blank" rel="noreferrer" className="text-primary hover:underline">{DOCS_URL}</a>.
          Campaigns and PWAs made through a key appear in Tracker → Campaigns / PWAs under the key's owner.
        </p>
      </div>

      <PublicApiKeys />

      <div className="bg-surface border border-border rounded-lg">
        <div className="px-6 py-4 border-b border-border">
          <h2 className="text-lg font-medium text-content">Provisioned PWAs</h2>
          <p className="text-sm text-content-subtle mt-1">
            Every <code className="text-xs">POST /pwa</code> call{showOwner ? ' by anyone' : ' made with your keys'}, newest first.
            The link is the one the call returned; a tracker PWA also has its campaign in the tracker.
          </p>
        </div>
        <div className="px-6 py-5">
          {isLoading ? (
            <p className="text-sm text-content-subtle">Loading…</p>
          ) : isError ? (
            <p className="text-sm text-danger">{(error as Error).message}</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-content-subtle">Nothing provisioned yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-content-subtle border-b border-border">
                    <th className="py-2 pr-4 font-medium">When</th>
                    <th className="py-2 pr-4 font-medium">Project</th>
                    <th className="py-2 pr-4 font-medium">GEO</th>
                    <th className="py-2 pr-4 font-medium">Platform</th>
                    <th className="py-2 pr-4 font-medium">Kind</th>
                    <th className="py-2 pr-4 font-medium">Offer</th>
                    <th className="py-2 pr-4 font-medium">Link</th>
                    <th className="py-2 pr-4 font-medium">Key</th>
                    {showOwner && <th className="py-2 pr-4 font-medium">Owner</th>}
                    <th className="py-2 font-medium">Ids</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-b border-border last:border-0 align-top">
                      <td className="py-2 pr-4 text-content-muted whitespace-nowrap">{formatDateTime(r.created_at)}</td>
                      <td className="py-2 pr-4 text-content">{r.project_name ?? r.project_id}</td>
                      <td className="py-2 pr-4 text-content">{r.geo}{r.language_code ? <span className="text-content-subtle"> · {r.language_code}</span> : null}</td>
                      <td className="py-2 pr-4 text-content">{PLATFORM_LABEL[r.platform]}</td>
                      <td className="py-2 pr-4 text-content-muted">{r.kind}</td>
                      <td className="py-2 pr-4 text-content-muted">{r.offer_partner ?? (r.offer_id ? r.offer_id : '—')}</td>
                      <td className="py-2 pr-4">
                        <a href={r.url} target="_blank" rel="noreferrer" className="text-primary hover:underline font-mono text-xs break-all">{r.url}</a>
                      </td>
                      <td className="py-2 pr-4 text-content-muted whitespace-nowrap">{r.api_key_name ?? (r.api_key_id ? r.api_key_id.slice(0, 8) : 'dashboard')}</td>
                      {showOwner && (
                        <td className="py-2 pr-4 text-content-muted whitespace-nowrap">{r.owner_name || r.owner_email || '—'}</td>
                      )}
                      <td className="py-2 font-mono text-[11px] text-content-subtle whitespace-nowrap">
                        <div>pwa {r.pwa_id.slice(0, 8)}</div>
                        {r.campaign_id && <div>campaign {r.campaign_id.slice(0, 8)}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
